'use client'

import { motion } from 'framer-motion'
import { MapPin, Code, Users, Calendar, Globe, Route } from '@/ui/icons'
import { 
  Footer,
  Navigation
} from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { Button, Logo } from '@/ui/components/'
import { getOptimizedImageUrl } from '@/shared/utils/media'
import { PageMeta } from '@/components/PageMeta'

export default function AboutUs() {
  // Navigation configuration
  const navigationItems = [
    {
      title: "Home",
      href: "/",
    },
  ]

  const navigationActions = {
    primary: { label: "Get started", href: "/signup" },
    secondary: { label: "Sign in", href: "/login" }
  }

  // Footer configuration
  const footerSections = [
    {
      title: "Product",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Sign Up", href: "/signup" },
      ]
    },
    {
      title: "Support",
      links: [
        { label: "Documentation", href: "/docs" },
        { label: "Contact Us", href: "/contact" },
        { label: "Report Issue", href: "/contact" },
      ]
    },
    {
      title: "Legal",
      links: [
        { label: "Terms of Service", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
      ]
    }
  ]

  return (
    <PageLayout showHeader={false}>
      <PageMeta title="About" description="Learn about SitRepp.io — collaborative overlanding trip planning" />
      <Navigation
        logo={<Logo to="/" size="md" />}
        items={navigationItems}
        actions={navigationActions}
      />

      <div className="min-h-screen bg-earth-50">
        {/* Hero Section - Large Heading */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center max-w-4xl mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                Passionate about overlanding
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                And we're on a mission to empower every overlanding crew to plan better trips, stay organized, and explore with confidence all from one collaborative platform.
              </p>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              className="w-full aspect-video rounded-2xl overflow-hidden shadow-xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about1.webp", 1200, 675, 85)}
                alt="Overlanding adventure - Hero image"
                className="w-full h-full object-cover"
                loading="lazy"
                width={1200}
                height={675}
              />
            </motion.div>
          </div>
        </section>

        {/* The Problem - Two Column Layout */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  Group chats turn into chaos
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    SitRepp.io was created to solve a problem familiar to anyone who plans trips with a crew: group chats turn into chaos. Details get buried, plans get repeated, and it's impossible to keep everyone aligned.
                  </p>
                  <p>
                    There had to be a better way to coordinate routes, share coordinates, track gear, and keep the whole crew on the same page even when the trail gets unpredictable.
                  </p>
                </div>
              </motion.div>

              {/* Image */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-full aspect-square rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about5.webp", 800, 800, 85)}
                    alt="Group coordination challenges in overlanding"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={800}
                  />
              </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Solution - Two Column Layout (Reversed) */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Image */}
              <motion.div
                className="relative lg:order-1"
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about3.webp", 1000, 750, 85)}
                    alt="Organized overlanding trip planning"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1000}
                    height={750}
                  />
        </div>
              </motion.div>

              {/* Text Content */}
              <motion.div
                className="lg:order-2"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  One place to organize everything
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    SitRepp.io brings order to that noise by giving overlanding groups one place to organize routes, share coordinates, upload photos, track gear, and stay synced even when the trail gets unpredictable.
                  </p>
                  <p>
                    The name comes from the idea of a "situation report" a quick summary that gets everyone on the same page. That's exactly what the platform aims to provide: clear, simple updates on where things stand so every trip feels organized, safe, and connected.
                  </p>
              </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Origin - Two Column Layout */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  Built by an overlander, for overlanders
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    SitRepp.io was started by an overlander who experienced the same frustrations firsthand. After countless camping trips and countless conversations in cluttered group chats, it became obvious that the community needed something built specifically for how overlanders actually plan.
                  </p>
                  <p>
                    While the outdoors space is filled with physical gear from rooftop tents to bumpers, lighting, and cooking setups there was a gap when it came to digital tools that supported the way crews communicate and prepare.
                  </p>
                  <p>
                    As a software engineer and active member of the overlanding community, he chose to contribute through code. The goal wasn't to build another map app it was to create a platform that strengthens the community itself by making it easy to share trips, collaborate, and bring people together.
                  </p>
                </div>
              </motion.div>

              {/* Image */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about2.webp", 600, 800, 85)}
                    alt="Overlanding community and adventure"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={600}
                    height={800}
                  />
                </div>
              </motion.div>
            </div>
            </div>
          </section>

        {/* Mission & Community - Two Column with Info Box */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Main Content */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  SitRepp.io is a place where crews thrive—together
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Seeing crews plan better trips, stay organized, and explore with confidence is what gets us out of bed every morning.
                  </p>
                  <p>
                    We're overlanders and developers just like our users, leveraging our own tools for better trip planning. And we learn from our community, too. You can be sure we'll share every insight we discover through the innovations we implement in our platform.
                  </p>
                  <p>
                    SitRepp.io is built for the crews who want to stay organized without losing the fun of exploring. For the people who welcome newcomers the same way the founder was welcomed when he started. And for every group that wants a smoother way to plan the next adventure.
                  </p>
                  <p className="font-medium text-foreground">
                    This is more than a tool—it's a community-driven platform designed to grow with the people who use it.
                  </p>
                </div>
              </motion.div>

              {/* Info Box */}
              <motion.div
                className="bg-earth-50 rounded-2xl p-8 lg:p-10 shadow-sm"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className="text-2xl font-bold text-foreground mb-4">About SitRepp.io</h3>
                <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                  <p>
                    SitRepp.io is built by overlanders, for overlanders. Our platform helps crews coordinate trips, manage gear, plan meals, and share memories—all in one collaborative workspace.
                  </p>
                  <p>
                    We're committed to building tools that strengthen the overlanding community and make trip planning easier, safer, and more connected.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Image Composition - Multiple Images */}
            <motion.div
              className="mt-16 relative"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 aspect-[16/10] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about11.webp", 1200, 750, 85)}
                    alt="Overlanding adventure and community"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={1200}
                    height={750}
                  />
                </div>
                <div className="space-y-6">
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about8.webp", 800, 1000, 85)}
                      alt="Overlanding crew and adventure"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width={800}
                      height={1000}
                    />
                  </div>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about10.webp", 800, 600, 85)}
                      alt="Overlanding trip planning and coordination"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width={800}
                      height={600}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* What We're Building */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center max-w-3xl mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                What we're building
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                We're creating a new standard for trip planning—a platform that empowers crews to rapidly plan and coordinate overlanding trips, without compromising on organization or safety.
              </p>
            </motion.div>

            {/* Two Column Feature Sections */}
            <div className="space-y-20 lg:space-y-32">
              {/* Feature 1 */}
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                    A way to plan more than a route
                  </h3>
                  <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                    <p>
                      We're giving crews a way to go beyond just mapping a route and instead deliver a complete trip plan. Our platform helps you organize routes, coordinate gear, plan meals, and share photos—all in one place.
                    </p>
                    <p>
                      Naturally, we had to make the experience of planning trips a dream. So we built features that work together seamlessly, allowing crews to coordinate everything from their local planning workflow to the trail.
              </p>
            </div>
                </motion.div>

                <motion.div
                  className="relative"
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/campingstill3_1.3.1.webp", 1000, 750, 85)}
                      alt="Complete trip planning platform"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width={1000}
                      height={750}
                    />
                  </div>
                </motion.div>
              </div>

              {/* Feature 2 */}
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <motion.div
                  className="relative lg:order-1"
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={getOptimizedImageUrl("https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/about6.webp", 1000, 750, 85)}
                      alt="Collaborative trip coordination platform"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width={1000}
                      height={750}
                    />
            </div>
                </motion.div>

                <motion.div
                  className="lg:order-2"
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                    A way to coordinate without compromise
                  </h3>
                  <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                    <p>
                      With our collaborative platform, crews can find the independence they want when planning trips. No more compromising on the benefits of staying organized or keeping everyone informed.
                    </p>
                    <p>
                      Our platform gives crews the control they want when coordinating trips without the distracting fears of missing details or losing track of important information.
              </p>
            </div>
                </motion.div>
              </div>
            </div>
            </div>
          </section>

          {/* Call to Action */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Ready to start planning your next adventure?
            </h2>
              <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
                Join the SitRepp community and start coordinating your overlanding trips with ease.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="text-lg px-8 py-6">
                <a href="/signup">Get Started</a>
              </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6">
                <a href="/contact">Contact Us</a>
              </Button>
              </div>
            </motion.div>
            </div>
          </section>
      </div>

      <Footer
        sections={footerSections}
        description="A collaborative workspace for overlanding crews. Coordinate trips, manage gear, plan meals, and share memories—all in one place."
      />
    </PageLayout>
  )
}
